import { api } from './api-client';

/**
 * The language catalogue, as `/v1/languages` serves it.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/languages/`.
 *
 * Ordering arrived with the app needing to know which three languages to lead
 * with — see ADR 0007. Availability and per-language content settings are still
 * expected, and each will arrive with the decision that justifies it rather
 * than as a column nobody fills.
 */
export interface Language {
  id: string;
  /** ISO 639-1, lowercase as stored. Displayed uppercase. */
  code: string;
  /** English name. */
  name: string;
  /** The name in the language itself — Italiano, العربية. */
  nativeName: string;
  /**
   * Where this sits in the list learners are offered. Lower comes first.
   *
   * Null means unranked, which is a different state from ranked zero: it sorts
   * to the back rather than the front.
   */
  displayOrder: number | null;
  /**
   * The synthesiser voice this language is spoken in by default — a provider
   * name such as `es-ES-Neural2-A`. See ADR 0013.
   *
   * Null means nobody has chosen one, and that is a real state rather than a
   * missing value: the language has no audio and the app shows no play button.
   * A scene may override it; clearing this is how an operator takes the voice
   * away again.
   */
  ttsVoice: string | null;
  /**
   * The sentence Act III falls back to when no level could be estimated for a
   * conversation in this language. See ADR 0014.
   *
   * Null means the server's own default, the same third state `ttsVoice` uses.
   * Worth wording per language because the fallback is not shown evenly: it
   * appears whenever the model fails, which is most often in the languages the
   * model is weakest in.
   */
  replayFallback: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the add and edit forms produce. */
export interface LanguageDraft {
  code: string;
  name: string;
  nativeName: string;
  displayOrder: number | null;
  ttsVoice: string | null;
  replayFallback: string | null;
}

export async function listLanguages(): Promise<Language[]> {
  const { languages } = await api.get<{ languages: Language[] }>('/admin/languages');
  return languages;
}

export async function createLanguage(draft: LanguageDraft): Promise<Language> {
  const { language } = await api.post<{ language: Language }>('/admin/languages', draft);
  return language;
}

export async function updateLanguage(
  id: string,
  draft: LanguageDraft,
): Promise<Language> {
  const { language } = await api.patch<{ language: Language }>(
    `/admin/languages/${id}`,
    draft,
  );
  return language;
}

export function deleteLanguage(id: string): Promise<void> {
  return api.del(`/admin/languages/${id}`);
}
